import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CallAPI } from 'actions/General';
import TableComponent from 'components/table-component/Table';
import {
  createUpdateRecordStructure,
  formsMetaSQLHeaderList,
  formsStructureHeaderList,
} from '../utils';
import { cloneDeep, difference, isEqual } from 'lodash';
import FormsSetsHeaders from './FormsSetsHeaders';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Pagination from 'components/common-components/Pagination';

const FormMetaAndSQL = ({ type }) => {
  const { t } = useTranslation();
  const [selectedFormSets, setSelectedFormSets] = useState([]);
  const [formSets, setFormSets] = useState([]);
  const [topics, setTopics] = useState([]);
  const [formSetsListing, setFormSetsListing] = useState([]);
  const [metaDataList, setMetaDataList] = useState([]);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [headersList, setHeadersList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [loader, setLoader] = useState(false);
  const [updateApiBody, setUpdateApiBody] = useState(getDefaultApiBody);
  const [listingAPIOptions, setListingAPIOptions] = useState({
    sortBy: '',
    sortDirection: 'asc',
    searchText: '',
  });
  const [isHeaderNeedToChange, setIsHeaderNeedToChange] = useState(true);
  const [unSavedChanges, setUnSavedChanges] = useState(false);

  function getDefaultApiBody() {
    return {
      modifiedData: {},
      addedSetsList: [],
      removedSetsList: [],
    };
  }

  const getMetaDataList = async (page = 1, limit = displayEntries) => {
    setSelectedRecordIds([]);
    setSelectedRows([]);
    setUpdateApiBody(getDefaultApiBody());
    const metaDataResult = await CallAPI(
      'FETCH_META_DATA',
      { page: page, limit: limit, type: type, ...listingAPIOptions },
      null,
      setLoader,
    );
    if (metaDataResult.status) {
      const data = metaDataResult.data.rows;
      data.forEach((value, index) => {
        value.indexed = (page - 1) * limit + (index + 1);
      });
      setMetaDataList(data);
      if (page === 1) setTotalRows(metaDataResult.data.totalCount);
    } else {
      setMetaDataList([]);
      toast.error(metaDataResult.error);
    }
  };
  const getSetList = async () => {
    const setsResult = await CallAPI(
      type === 'forms_structure'
        ? 'FETCH_QUESTION_SETS_FOR_SELECTION'
        : 'FETCH_FORM_SETS_FOR_SELECTION',
      null,
      null,
    );
    if (setsResult.status) {
      const sortedResult = setsResult.data.map((singleSet) => {
        const sortedOptions = [...(singleSet.options || [])].sort((a, b) =>
          a.optionValue.localeCompare(b.optionValue, ['ar', 'en'], {
            sensitivity: 'base',
          }),
        );
        return {
          ...singleSet,
          options: sortedOptions,
        };
      });

      const formListing = sortedResult.map((singleSet) => {
        return { value: singleSet.sqlColumn.toLowerCase(), displayValue: singleSet.title };
      });
      setFormSetsListing(formListing);
      setFormSets(sortedResult);
    }
  };
  const getTopicList = async () => {
    const result = await CallAPI('FETCH_TOPICS_FOR_SELECTION', null, null);
    if (result.status) {
      result.data.sort((a, b) =>
        a.title.localeCompare(b.title, ['ar', 'en'], {
          sensitivity: 'base',
        }),
      );
      setTopics(result.data);
    }
  };

  const changeDisplayEntries = (name, val) => {
    setCurrentPage(1);
    setDisplayEntries(parseInt(val));
    getMetaDataList(1, parseInt(val));
  };

  useEffect(() => {
    setDisplayEntries(() => 10);
    setIsHeaderNeedToChange(() => true);
    setMetaDataList(() => []);
    setFormSetsListing(() => []);
    setFormSets(() => []);
    setCurrentPage(1);
    if (type === 'forms_structure') getTopicList();
    getSetList();
    getMetaDataList(1);
  }, [type]);

  useEffect(() => {
    setCurrentPage(1);
    getMetaDataList(1);
  }, [listingAPIOptions]);

  const updateListingApiParams = (updateObj) => {
    setListingAPIOptions({ ...listingAPIOptions, ...updateObj });
  };

  const updateTableData = async (
    form_id,
    sql_column,
    field_id,
    sql_type,
    setId,
    selectedId,
    selectedValue,
  ) => {
    const isDBField =
      type === 'form_meta_and_sql' && (sql_column === 'dbName' || sql_column === 'dbConnection');

    if (!isDBField) {
      const updatedMetaDataApiBody = createUpdateRecordStructure(
        updateApiBody.modifiedData,
        form_id,
        sql_column,
        field_id,
        sql_type,
        setId,
        selectedId,
        selectedValue,
      );

      setUpdateApiBody({
        modifiedData: {
          ...updatedMetaDataApiBody,
        },
        ...updateApiBody,
      });
    }

    setMetaDataList((prev) =>
      prev.map((singleRecord) => {
        if (
          (type === 'form_meta_and_sql' && singleRecord.formId === form_id) ||
          (type === 'forms_structure' && field_id === singleRecord.element_global_id)
        ) {
          return {
            ...singleRecord,
            [sql_column]: selectedValue,
          };
        }
        return singleRecord;
      }),
    );
  };

  const getHeaderColum = (name) => {
    let displayName = '';
    formSetsListing.map((singleSet) => {
      if (name === singleSet.value) displayName = singleSet.displayValue;
    });
    return {
      name: name,
      displayName: displayName,
      type: 'string',
      isRequired: false,
      isSelected: true,
      isFilterAble: true,
      associatedKey: null,
      populateFunc: 'populateSetSelection',
      sendRowWithKeyValue: true,
      callbackFunc: 'setsDetail',
      setType: type === 'forms_structure' ? 'questionSet' : 'formSet',
    };
  };

  const handleDBConnectionUpdate = async (rowData, value) => {
    const formId = rowData.formId;
    const dbName = rowData.dbName;

    if (value && dbName) {
      const result = await CallAPI('CHECK_EXTERNAL_DB_TABLE_NAME', { tableName: dbName });

      if (result.status) {
        if (result.data.tableExist) {
          toast.error(`Table: "${dbName}" already exists`);
          return;
        }
      } else {
        toast.error(result.error);
      }
    }

    const payload = {
      formId,
      externalVerticalExportSettings: value
        ? [
            {
              requestType: 'sql_db',
              settings: {
                externalDbTableName: dbName,
                enabledForExport: value,
              },
            },
          ]
        : [],
    };

    const result = await CallAPI('SAVE_FORM', payload, null, setLoader);

    if (result.status) {
      getMetaDataList(currentPage, displayEntries);
    } else {
      toast.error(result.error);
    }
  };

  useEffect(() => {
    const tableHeadersList =
      type === 'forms_structure' ? formsStructureHeaderList : formsMetaSQLHeaderList;
    if (metaDataList.length > 0 && formSetsListing.length > 0) {
      let selectedSetslist = [];
      let setHeaderSetColumn = [];
      const allSetRowsSQLColumn = formSetsListing.map((singleSet) => singleSet.value);
      Object.keys(metaDataList[0]).map((singleMetaDataKey) => {
        if (allSetRowsSQLColumn.includes(singleMetaDataKey.toLowerCase())) {
          selectedSetslist.push(singleMetaDataKey.toLowerCase());
          setHeaderSetColumn.push(getHeaderColum(singleMetaDataKey.toLowerCase()));
        }
      });
      if (isHeaderNeedToChange) {
        setHeadersList([...tableHeadersList, ...setHeaderSetColumn]);
        setSelectedFormSets(selectedSetslist);
        setIsHeaderNeedToChange(false);
      }
    } else {
      setHeadersList([...tableHeadersList]);
    }
  }, [formSetsListing, metaDataList]);

  const onRowsSelection = (name, value) => {
    let updatedSelectedIds = [...selectedRecordIds];
    let updatedSelectedRows = [...selectedRows];
    if (name === 'all') {
      if (value) {
        updatedSelectedIds = metaDataList.map((row) =>
          type === 'forms_structure' ? row.element_global_id : row.formId,
        );
        updatedSelectedRows = metaDataList.map((row) => ({
          _id: type === 'forms_structure' ? row.element_global_id : row.formId,
          form_id: type === 'forms_structure' ? row.form_id : row.formId,
          status: row.status,
        }));
      } else {
        updatedSelectedIds = [];
        updatedSelectedRows = [];
      }
    } else {
      const row = metaDataList.find((r) =>
        type === 'forms_structure' ? r.element_global_id === name : r.formId === name,
      );
      if (value) {
        if (!updatedSelectedIds.includes(name)) {
          updatedSelectedIds.push(name);
          updatedSelectedRows.push({
            _id: type === 'forms_structure' ? row.element_global_id : row.formId,
            form_id: type === 'forms_structure' ? row.form_id : row.formId,
            status: row.status,
          });
        }
      } else {
        updatedSelectedIds = updatedSelectedIds.filter((id) => id !== name);
        updatedSelectedRows = updatedSelectedRows.filter((r) => r._id !== name);
      }
    }
    setSelectedRecordIds(updatedSelectedIds);
    setSelectedRows(updatedSelectedRows);
  };
  const updateHeader = (setsList) => {
    let setHeaderSetColumn = [];
    let addedSetsList = updateApiBody.addedSetsList ?? [];
    let removedSetsList = updateApiBody.removedSetsList ?? [];

    setsList.map((singleSet) => {
      const isAlreadyEixst =
        headersList.filter((singleHeaderColumn) => singleHeaderColumn.name === singleSet).length >
        0;
      if (!isAlreadyEixst) {
        setHeaderSetColumn.push(getHeaderColum(singleSet));
        if (!addedSetsList.includes(singleSet)) {
          addedSetsList.push(singleSet);
        }
      }
    });
    const allHeaderColumns = [...headersList, ...setHeaderSetColumn];

    setSelectedFormSets([...setsList]);
    const allSetsSQLColumn = formSetsListing.map((singleSet) => singleSet.value);

    for (let index = 0; index < allHeaderColumns.length; index++) {
      const element = allHeaderColumns[index];
      if (allSetsSQLColumn.includes(element.name) && !setsList.includes(element.name)) {
        allHeaderColumns.splice(index, 1);
        if (addedSetsList.includes(element.name)) {
          addedSetsList.splice(addedSetsList.indexOf(element.name), 1);
          removedSetsList.push(element.name);
        } else if (!removedSetsList.includes(element.name)) {
          removedSetsList.push(element.name);
        }
        index--;
      }
    }

    removedSetsList = difference(removedSetsList, setsList);

    const cleanedModifiedData = {};
    const stateModifiedData = cloneDeep({ ...updateApiBody.modifiedData });
    for (let key in stateModifiedData) {
      for (let subKey in stateModifiedData[key]) {
        if (removedSetsList.indexOf(subKey) === -1) {
          if (!cleanedModifiedData[key]) cleanedModifiedData[key] = {};
          cleanedModifiedData[key][subKey] = stateModifiedData[key][subKey];
        }
      }
    }

    setUpdateApiBody({
      ...updateApiBody,
      modifiedData: cleanedModifiedData,
      addedSetsList,
      removedSetsList,
    });

    setHeadersList(allHeaderColumns);
  };

  const handleSync = async () => {
    const result = await CallAPI(
      'UPDATE_FORM_STRUCTURE_META_DATA',
      { ...updateApiBody, type },
      null,
      setLoader,
    );

    if (result.status) {
      setUpdateApiBody(getDefaultApiBody());
    }
  };

  useEffect(() => {
    const rest = cloneDeep(updateApiBody);
    delete rest.type;
    const result = !isEqual(rest, getDefaultApiBody());
    setUnSavedChanges(result);
  }, [updateApiBody]);

  return (
    <div>
      <FormsSetsHeaders
        selectedRecordIds={selectedRecordIds}
        selectedRows={selectedRows}
        actionsCallBack={getMetaDataList}
        handleManageSets={updateHeader}
        selectedFormSets={selectedFormSets}
        setsOptions={formSetsListing}
        handleSync={handleSync}
        selectedTab={type}
        headersList={headersList}
        listingAPIOptions={listingAPIOptions}
        updateListingApiParams={updateListingApiParams}
        topicList={topics}
        formSets={formSets}
        updateApiBody={updateApiBody}
        setUpdateApiBody={setUpdateApiBody}
        setMetaDataList={setMetaDataList}
        unSavedChanges={unSavedChanges}
      />
      <div className='dashboard-setup-table'>
        <TableComponent
          header={headersList}
          data={loader ? [] : metaDataList}
          totalRecords={totalRows}
          loader={loader}
          isLoading={loader}
          filterEnabled={false}
          isActionsEnabled={false}
          isSelectionEnabled={true}
          selectionKey={type === 'forms_structure' ? 'element_global_id' : 'formId'}
          selectedRows={selectedRecordIds}
          onSelection={onRowsSelection}
          props={{
            setsDetail: {
              updateTableData,
              handleDBConnectionUpdate,
              setsList: formSets,
              topicsList: topics,
            },
            translateValueFunc: (val) => t(val),
          }}
          isPaginationEnabled={false}
          isFieldSelectionEnabled={true}
        />
      </div>
      {totalRows && currentPage ? (
        <Pagination
          totalRecords={totalRows}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          displayEntries={displayEntries}
          setDisplayEntries={changeDisplayEntries}
          currentPageRecords={metaDataList ? metaDataList.length : 0}
          paginationFunction={getMetaDataList}
        />
      ) : null}
    </div>
  );
};
FormMetaAndSQL.propTypes = {
  type: PropTypes.string,
};
export default FormMetaAndSQL;
